import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ResourceServerSummaryComponent } from '../../dataset/resource-server-summary/resource-server-summary.component';
import { BasicInfoComponent } from '../../dataset/ngsild/basic-info/basic-info.component';
import { NgsildItemService } from '../../dataset/ngsild/ngsild-item.service';
import { CatalogueApiService } from '@angular-workspace/services';
import {
  getApiErrorDetail,
  getApiErrorStatus,
} from '../../dataset/ngsild/ngsild-api-error.util';
import type {
  ModelBasicInfo,
  ModelMetadataInfo,
  ModelOnboardingResult,
} from '../model.models';
import { ModelMetadataInfoComponent } from '../model-metadata-info/model-metadata-info.component';
import { ModelMetadataJsonUploadComponent } from '../model-metadata-json-upload/model-metadata-json-upload.component';
import { parseModelMetadataToOnboarding } from '../model-metadata-template.util';
import { parseTemplateAdditionalInfo } from '../../shared/additional-info/additional-info.logic';

const STEP_QUERY_KEY = 'step';

interface JsonMetadataStep {
  key: string;
  label: string;
}

@Component({
  selector: 'lib-model-metadata-json-home',
  standalone: true,
  imports: [
    CommonModule,
    ModelMetadataJsonUploadComponent,
    BasicInfoComponent,
    ModelMetadataInfoComponent,
    ResourceServerSummaryComponent,
  ],
  templateUrl: './model-metadata-json-home.component.html',
  styleUrl: '../../dataset/metadata-json/metadata-json-home.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelMetadataJsonHomeComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngsildItemService = inject(NgsildItemService);
  private readonly catalogueApi = inject(CatalogueApiService);

  readonly uploadRef = viewChild(ModelMetadataJsonUploadComponent);
  readonly basicInfoRef = viewChild(BasicInfoComponent);
  readonly metadataInfoRef = viewChild(ModelMetadataInfoComponent);

  @Input() publisherRole: 'org-admin' | 'provider' = 'provider';

  readonly back = output<void>();
  readonly complete = output<ModelOnboardingResult>();
  readonly submitError = output<string>();

  readonly steps: JsonMetadataStep[] = [
    { key: 'upload', label: 'Upload metadata' },
    { key: 'preview', label: 'Preview or edit metadata' },
  ];

  readonly currentStep = signal(0);
  readonly basicValid = signal(false);
  readonly metadataValid = signal(false);
  readonly isSubmitting = signal(false);

  ngOnInit(): void {
    const raw = this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY);
    this.currentStep.set(raw === '1' ? 1 : 0);
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        const next = params.get(STEP_QUERY_KEY) === '1' ? 1 : 0;
        if (this.currentStep() !== next) {
          this.currentStep.set(next);
          if (next === 1) {
            queueMicrotask(() => this.applyPreviewFromUpload());
          }
        }
      });
  }

  isStepActive(index: number): boolean {
    return this.currentStep() === index;
  }

  isStepCompleted(index: number): boolean {
    return index < this.currentStep();
  }

  isStepClickable(index: number): boolean {
    if (index === 0) return true;
    return index === 1 && !!this.uploadRef()?.hasValidUpload();
  }

  onStepClick(index: number): void {
    if (!this.isStepClickable(index)) return;
    this.currentStep.set(index);
    this.syncStepQueryParam();
    if (index === 1) {
      queueMicrotask(() => this.applyPreviewFromUpload());
    }
  }

  onBasicValidChange(valid: boolean): void {
    this.basicValid.set(valid);
  }

  onMetadataValidChange(valid: boolean): void {
    this.metadataValid.set(valid);
  }

  canGoNext(): boolean {
    if (this.isSubmitting()) return false;
    if (this.currentStep() === 0) {
      return !!this.uploadRef()?.hasValidUpload();
    }
    return this.basicValid() && this.metadataValid();
  }

  nextButtonLabel(): string {
    if (this.isSubmitting()) return 'Creating model…';
    return this.currentStep() === 0 ? 'Next →' : 'Submit';
  }

  onBackClick(): void {
    if (this.currentStep() > 0) {
      this.currentStep.update((s) => s - 1);
      this.syncStepQueryParam();
      return;
    }
    this.back.emit();
  }

  async onNextClick(): Promise<void> {
    const step = this.currentStep();

    if (step === 0) {
      if (!this.uploadRef()?.hasValidUpload()) return;
      this.currentStep.set(1);
      this.syncStepQueryParam();
      queueMicrotask(() => this.applyPreviewFromUpload());
      return;
    }

    this.basicInfoRef()?.form.markAllAsTouched();
    this.metadataInfoRef()?.form.markAllAsTouched();
    if (!this.canGoNext()) return;
    if (!this.metadataInfoRef()?.validateAdditionalInfo()) return;

    const basic = this.buildBasicPayload();
    const metadata = this.buildMetadataPayload();
    if (!basic || !metadata) return;

    this.isSubmitting.set(true);
    try {
      const result = await this.ngsildItemService.createModelItem({
        basic,
        metadata,
      });
      await this.submitAdditionalInfo(result.itemId);
      this.basicInfoRef()?.resetForm();
      this.metadataInfoRef()?.resetForm();
      this.uploadRef()?.removeFile();
      this.currentStep.set(0);
      this.syncStepQueryParam();
      this.complete.emit({
        payload: result.payload,
        itemId: result.itemId,
      });
    } catch (error) {
      console.error('Failed to create model from metadata JSON:', error);
      const status = getApiErrorStatus(error);
      const detail = getApiErrorDetail(
        error,
        status === 409
          ? 'Item with this name already exists in local catalogue'
          : 'Failed to create model. Please try again.',
      );
      this.submitError.emit(detail);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private applyPreviewFromUpload(): void {
    const json = this.uploadRef()?.getParsedJson();
    if (!json) return;
    try {
      const payload = parseModelMetadataToOnboarding(json);
      this.basicInfoRef()?.applyValue(payload.basic as ModelBasicInfo);
      this.metadataInfoRef()?.applyValue(payload.metadata);
      this.metadataInfoRef()?.applyAdditionalInfo(
        parseTemplateAdditionalInfo(json),
      );
    } catch (error) {
      console.error('Failed to map JSON to preview forms:', error);
    }
  }

  private async submitAdditionalInfo(assetId: string): Promise<void> {
    const items = this.metadataInfoRef()?.getAdditionalInfoItems() ?? [];
    for (const item of items) {
      try {
        await this.catalogueApi.postProviderFeedback({
          assetId,
          type: item.type,
          data: item.data,
        });
      } catch (error) {
        console.error('Failed to submit additional info item:', error);
      }
    }
  }

  private buildBasicPayload(): ModelBasicInfo | null {
    const ref = this.basicInfoRef();
    if (ref) {
      return ref.form.getRawValue() as ModelBasicInfo;
    }
    return null;
  }

  private buildMetadataPayload(): ModelMetadataInfo | null {
    const ref = this.metadataInfoRef();
    if (ref) {
      return ref.form.getRawValue() as ModelMetadataInfo;
    }
    return null;
  }

  private syncStepQueryParam(): void {
    const step = this.currentStep();
    const next = String(step);
    if (this.route.snapshot.queryParamMap.get(STEP_QUERY_KEY) === next) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [STEP_QUERY_KEY]: step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }
}
